import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAstrologerProfile } from '../../hooks/useAstrologerProfile';
import type { AstrologerAnalysis, AstrologerClient } from '../../types/professional';

interface RecentActivity {
  id: string;
  service_name: string;
  client_name: string;
  created_at: string;
}

interface ServiceCount { service_name: string; count: number }

export function ProfessionalDashboard() {
  const navigate = useNavigate();
  const { astrologerProfile, loading } = useAstrologerProfile();
  const [activity,      setActivity]      = useState<RecentActivity[]>([]);
  const [topServices,   setTopServices]   = useState<ServiceCount[]>([]);
  const [weekCount,     setWeekCount]     = useState(0);
  const [loadingData,   setLoadingData]   = useState(true);

  useEffect(() => {
    if (!astrologerProfile) {
      if (!loading) setLoadingData(false);
      return;
    }

    // Redirect to wizard if not completed
    if (!astrologerProfile.wizard_completed) {
      navigate('/profissional/ativar');
      return;
    }

    async function loadData() {
      const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString();

      const [{ data: analyses }, { data: clients }] = await Promise.all([
        supabase
          .from('astrologer_client_analyses')
          .select('id, service_name, client_id, created_at, status')
          .eq('astrologer_id', astrologerProfile!.id)
          .order('created_at', { ascending: false })
          .limit(20),
        supabase
          .from('astrologer_clients')
          .select('id, name')
          .eq('astrologer_id', astrologerProfile!.id)
          .eq('is_active', true),
      ]);

      const clientMap: Record<string, string> = {};
      for (const c of (clients ?? []) as AstrologerClient[]) {
        clientMap[c.id] = c.name;
      }

      const recent: RecentActivity[] = ((analyses ?? []) as AstrologerAnalysis[])
        .slice(0, 10)
        .map(a => ({
          id:           a.id,
          service_name: a.service_name,
          client_name:  clientMap[a.client_id] ?? 'Cliente',
          created_at:   a.created_at,
        }));

      setActivity(recent);

      // Week count
      const thisWeek = ((analyses ?? []) as AstrologerAnalysis[])
        .filter(a => a.created_at >= weekAgo).length;
      setWeekCount(thisWeek);

      // Top services
      const counts: Record<string, number> = {};
      for (const a of (analyses ?? []) as AstrologerAnalysis[]) {
        counts[a.service_name] = (counts[a.service_name] ?? 0) + 1;
      }
      const sorted = Object.entries(counts)
        .map(([service_name, count]) => ({ service_name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
      setTopServices(sorted);

      setLoadingData(false);
    }

    loadData();
  }, [astrologerProfile?.id, loading, navigate]);

  if (loading || loadingData) {
    return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Carregando painel...</div>;
  }

  if (!astrologerProfile) return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
        Nenhum perfil de astrólogo encontrado. Complete a ativação para acessar o portal.
      </p>
      <a href="/profissional/ativar" className="btn btn-primary btn-sm">Ativar conta</a>
    </div>
  );

  function relativeTime(iso: string) {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  }

  // Group activity by day
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();
  const todayItems = activity.filter(a => new Date(a.created_at).toDateString() === today);
  const yesterdayItems = activity.filter(a => new Date(a.created_at).toDateString() === yesterday);
  const olderItems = activity.filter(a => {
    const d = new Date(a.created_at).toDateString();
    return d !== today && d !== yesterday;
  });

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: 'clamp(1.25rem,3vw,1.625rem)', color: 'var(--text-primary)', margin: 0, fontFamily: 'var(--font-display)' }}>
          ✦ Painel Profissional
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)', margin: '0.25rem 0 0' }}>
          {astrologerProfile.professional_name}
        </p>
      </div>

      {/* KPI row */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        {[
          { label: 'Clientes',            value: astrologerProfile.total_clients },
          { label: 'Análises',            value: astrologerProfile.total_analyses },
          { label: 'Esta semana',         value: weekCount },
        ].map(({ label, value }) => (
          <div key={label} className="card" style={{ flex: '1 1 140px', textAlign: 'center' }}>
            <p style={{ fontSize: 'clamp(1.5rem,4vw,2rem)', fontWeight: 700, color: 'var(--petrol)', margin: '0 0 0.25rem' }}>
              {value}
            </p>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {label}
            </p>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem' }}>

        {/* Recent activity */}
        <div className="card">
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem', fontSize: 'var(--text-label)' }}>
            Atividade Recente
          </p>
          {activity.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>Nenhuma análise ainda.</p>
          ) : (
            <>
              {todayItems.length > 0 && (
                <>
                  <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '0.4rem' }}>Hoje</p>
                  {todayItems.map(a => (
                    <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.35rem 0', borderBottom: '1px solid var(--border-subtle)' }}>
                      <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
                        {a.service_name}, <span style={{ color: 'var(--text-primary)' }}>{a.client_name}</span>
                      </span>
                      <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{relativeTime(a.created_at)}</span>
                    </div>
                  ))}
                </>
              )}
              {yesterdayItems.length > 0 && (
                <>
                  <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '0.4rem', marginTop: '0.75rem' }}>Ontem</p>
                  {yesterdayItems.map(a => (
                    <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.35rem 0', borderBottom: '1px solid var(--border-subtle)' }}>
                      <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
                        {a.service_name}, <span style={{ color: 'var(--text-primary)' }}>{a.client_name}</span>
                      </span>
                      <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{relativeTime(a.created_at)}</span>
                    </div>
                  ))}
                </>
              )}
              {olderItems.length > 0 && (
                <>
                  <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '0.4rem', marginTop: '0.75rem' }}>Anteriores</p>
                  {olderItems.map(a => (
                    <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.35rem 0', borderBottom: '1px solid var(--border-subtle)' }}>
                      <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
                        {a.service_name}, <span style={{ color: 'var(--text-primary)' }}>{a.client_name}</span>
                      </span>
                      <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{relativeTime(a.created_at)}</span>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>

        {/* Top services */}
        <div className="card">
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem', fontSize: 'var(--text-label)' }}>
            Análises Mais Geradas
          </p>
          {topServices.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>Nenhuma análise gerada ainda.</p>
          ) : (
            <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {topServices.map((s, i) => (
                <li key={s.service_name} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.4rem 0', borderBottom: '1px solid var(--border-subtle)' }}>
                  <span style={{ fontWeight: 700, color: 'var(--petrol)', width: '1.2rem', flexShrink: 0 }}>{i + 1}.</span>
                  <span style={{ flex: 1, fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>{s.service_name}</span>
                  <span style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)' }}>{s.count}</span>
                </li>
              ))}
            </ol>
          )}

          <button
            className="btn-primary"
            style={{ width: '100%', marginTop: '1rem' }}
            onClick={() => navigate('/profissional/clientes')}
          >
            Ir para Clientes
          </button>
        </div>
      </div>
    </div>
  );
}
